import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, ArrowRight, ShieldCheck } from "lucide-react";
import PublicShell from "@/components/public/PublicShell";
import { api } from "@/lib/api";

/**
 * /testimonial-renewed?ref=<token>
 * Branded thank-you page rendered after the customer clicks the renewal
 * link from their reminder email. The backend endpoint updates the record;
 * this page just calls it and shows a confirmation.
 */
export default function TestimonialRenewed() {
    const [sp] = useSearchParams();
    const ref = sp.get("ref") || "";
    const [state, setState] = useState({ status: "loading", data: null, error: null });

    useEffect(() => {
        if (!ref) {
            setState({ status: "error", data: null, error: "Missing renewal reference." });
            return;
        }
        api.get(`/public/testimonials/renew-consent?ref=${encodeURIComponent(ref)}`)
            .then((r) => setState({ status: "ok", data: r.data, error: null }))
            .catch((err) => setState({
                status: "error", data: null,
                error: err?.response?.data?.detail || "This link is no longer valid.",
            }));
    }, [ref]);

    return (
        <PublicShell>
            <style>{styles}</style>
            <div className="tr-wrap" data-testid="testimonial-renewed">
                <div className="tr-card">
                    {state.status === "loading" && (
                        <div className="tr-loading" data-testid="tr-loading">
                            <div className="tr-spinner" />
                            <div className="tr-body">Confirming your consent…</div>
                        </div>
                    )}
                    {state.status === "ok" && (
                        <>
                            <CheckCircle2 size={44} className="tr-check" />
                            <div className="tr-eyebrow">CONSENT RENEWED</div>
                            <h1 className="tr-title">
                                Thank you{state.data?.customer_name ? `, ${state.data.customer_name}` : ""}.
                            </h1>
                            <p className="tr-body">
                                Your testimonial stays live on the VAULT public site until <strong>{state.data?.expires_at}</strong>.
                                We'll ping you again a week before that, so nothing surprises you.
                            </p>
                            {state.data?.quote && (
                                <blockquote className="tr-quote" data-testid="tr-quote-card">
                                    <div className="tr-quote-mark">&ldquo;</div>
                                    <p className="tr-quote-text">{state.data.quote}</p>
                                    <footer className="tr-quote-foot">
                                        <span className="tr-quote-author">{state.data.customer_name}</span>
                                        <span className="tr-quote-role">
                                            {[state.data.role, state.data.company, state.data.country].filter(Boolean).join(" · ")}
                                        </span>
                                    </footer>
                                </blockquote>
                            )}
                            <div className="tr-actions">
                                <Link to="/" className="tr-btn tr-btn--gold" data-testid="tr-home">
                                    Back to VAULT <ArrowRight size={14} />
                                </Link>
                                <Link to="/security" className="tr-btn tr-btn--ghost" data-testid="tr-security">
                                    <ShieldCheck size={13} /> Our consent policy
                                </Link>
                            </div>
                            <p className="tr-fine">
                                Changed your mind? Reply <em>REMOVE</em> to the reminder email or contact
                                privacy@vaultbypd.com — we'll drop the quote within one business day.
                            </p>
                        </>
                    )}
                    {state.status === "error" && (
                        <>
                            <div className="tr-eyebrow tr-eyebrow--warn">LINK EXPIRED</div>
                            <h1 className="tr-title">This renewal link has expired.</h1>
                            <p className="tr-body">{state.error}</p>
                            <p className="tr-body">
                                Your quote may have already lapsed off the public site. If you'd like it back
                                up, drop us a note at <a href="mailto:hello@vaultbypd.com">hello@vaultbypd.com</a>
                                {" "}and we'll send a fresh link.
                            </p>
                            <Link to="/" className="tr-btn tr-btn--gold" data-testid="tr-home">
                                Back to VAULT <ArrowRight size={14} />
                            </Link>
                        </>
                    )}
                </div>
            </div>
        </PublicShell>
    );
}

const styles = `
.tr-wrap { min-height: calc(100vh - 200px); display: flex; align-items: center; justify-content: center; padding: 80px 24px; color: #F6F3EC; }
.tr-card { max-width: 560px; width: 100%; background: rgba(246,243,236,0.04); border: 1px solid rgba(246,243,236,0.1); border-radius: 24px; padding: 48px 40px; text-align: center; }
.tr-check { color: #29C58A; margin: 0 auto 12px; }
.tr-eyebrow { font-size: 10.5px; letter-spacing: 0.24em; font-weight: 700; color: #C6A86B; text-transform: uppercase; margin-bottom: 12px; }
.tr-eyebrow--warn { color: #F87171; }
.tr-title { font-size: 30px; font-weight: 800; letter-spacing: -0.02em; line-height: 1.15; margin: 0 0 16px; }
.tr-body { font-size: 15px; line-height: 1.6; color: rgba(246,243,236,0.75); margin: 0 0 16px; }
.tr-actions { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; margin: 28px 0 20px; }
.tr-btn { display: inline-flex; align-items: center; gap: 8px; padding: 12px 22px; border-radius: 999px; font-size: 13.5px; font-weight: 700; }
.tr-btn--gold { background: #C6A86B; color: #0B1122; }
.tr-btn--gold:hover { background: #d3b978; }
.tr-btn--ghost { background: transparent; color: #F6F3EC; border: 1px solid rgba(246,243,236,0.2); }
.tr-btn--ghost:hover { border-color: rgba(246,243,236,0.45); }
.tr-fine { margin-top: 24px; font-size: 12px; color: rgba(246,243,236,0.5); line-height: 1.6; }
.tr-fine em { color: #C6A86B; font-style: normal; font-weight: 600; }
.tr-fine a { color: #C6A86B; }
.tr-quote { margin: 24px 0 20px; padding: 22px 24px; background: rgba(198,168,107,0.06); border: 1px solid rgba(198,168,107,0.24); border-radius: 16px; text-align: left; }
.tr-quote-mark { font-family: Georgia, serif; font-size: 44px; line-height: 0.6; color: #C6A86B; }
.tr-quote-text { margin: 6px 0 14px; font-size: 15px; line-height: 1.6; color: #F6F3EC; font-style: italic; }
.tr-quote-foot { display: flex; flex-direction: column; gap: 2px; font-size: 12px; }
.tr-quote-author { color: #C6A86B; font-weight: 700; letter-spacing: 0.02em; }
.tr-quote-role { color: rgba(246,243,236,0.55); }
.tr-loading { display: flex; flex-direction: column; align-items: center; gap: 16px; padding: 20px 0; }
.tr-spinner { width: 36px; height: 36px; border-radius: 50%; border: 3px solid rgba(246,243,236,0.15); border-top-color: #C6A86B; animation: tr-spin 0.9s linear infinite; }
@keyframes tr-spin { to { transform: rotate(360deg); } }
`;
